import { BlogBuilder } from "../components/blog/BlogBuilder";
import { projects } from "./config";

const commonIngestionPlatform = new BlogBuilder({
  title: "Common Ingestion Workflow: Scalable Two-Phase Data Pipeline",
//   image: require("../components/blog/PVS/arch.png"),
  description: "Event-driven serverless ingestion platform consolidating 100+ production machine data with 73% cost reduction and 99.9% uptime. These machines are the customer owner, company owned and engineering large vehicles.",
})
  .setPublished("04 December 2025")
  .setReadTime(12)
  .setAuthor({
    name: "Shubham Gupta",
    logo: require("../editable-stuff/prof.jpg"),
    designation: "Lead Data Engineer",
    company: "Deere & Co",
  })

  // ============================================
  // PROJECT OVERVIEW SECTION
  // ============================================
  .addHeading("Project Overview")
  .addParagraph(
    "Designed and deployed a serverless, event-driven data ingestion platform that consolidates robotics field data from 1000+ machines into a centralized enterprise data lake. The solution reduced operational costs by 73%, decreased processing time by 96%, and improved data quality scores from 92% to 98%."
  )
//   .addImageBlock(
//     require("../components/blog/PVS/arch.png"),
//     'at_txt',
//     'Three-phase ingestion pipeline: Data Ingestion → Extraction & Processing → Downstream Consumption',
//     { size: 'contained', align: 'center' }
//   )

  // KEY METRICS TABLE
  .addHeading("Key Metrics & Impact")
  .addTable({
    headers: ["Metric", "Value", "Impact"],
    rows: [
      ["Monthly Cost Reduction", "73% ($129.6K/year)", "Bottom-line impact"],
      ["Processing Speed Improvement", "96% faster (48h → 2h)", "Real-time insights"],
      ["Data Quality Score", "98% validation success", "Enterprise-grade reliability"],
      ["Availability SLA", "99.9% uptime", "Production-ready"],
      ["Team Productivity Gain", "83% less manual work", "Better developer experience"],
    ]
  })

  // ============================================
  // PROBLEM & CONTEXT SECTION
  // ============================================
  .addHeading("The Challenge: Before Implementation")
  .addParagraph(
    "When I inherited this project, the organization faced critical bottlenecks that impacted data quality, team productivity, and operational costs."
  )

  .addSubHeading("Challenges Identified")
  .addBulletList([
    "Data Fragmentation - Each team maintained separate extraction logic (3+ different scripts) causing inconsistency",
    "Manual Overhead - 60+ hours/month spent on data validation and error recovery",
    "Quality Issues - Data inconsistencies and schema mismatches causing downstream failures",
    "Cost Inefficiency - Unoptimized AWS usage with 10+ separate S3 buckets and always-on EC2 instances ($14.8K/month)",
    "No Visibility - Failures went undetected for hours, impacting downstream teams and decision-making"
  ])

  .addSubHeading("Vision & Objectives")
  .addParagraph(
    "The goal was to create a unified, scalable, cost-effective ingestion platform that would serve as the backbone for all robotics data processing."
  )
  .addBulletList([
    "✅ Consolidate all robotics machine data into single source of truth",
    "✅ Automate 95%+ of data validation and processing",
    "✅ Provide real-time error visibility and alerting",
    "✅ Enable rapid onboarding of new data sources",
    "✅ Reduce operational costs by 50%+"
  ])

  // ============================================
  // ARCHITECTURE SECTION
  // ============================================
  .addHeading("Architecture & System Design")
  .addParagraph(
    "The solution is architected as a three-phase pipeline, with each phase handling specific responsibilities while maintaining loose coupling through well-defined interfaces."
  )
//   .addImageBlock(
//     require("../components/blog/PVS/arch.png"),
//     'at_txt',
//     'High-level system design showing data flow through three phases',
//     { size: 'contained', align: 'center' }
//   )

  // ============================================
  // PHASE 1: DATA INGESTION
  // ============================================
  .addHeading("Phase 1: Data Ingestion & Validation")
  .addParagraph(
    "The entry point for all incoming data, Phase 1 focuses on landing raw data safely, extracting metadata, validating prerequisites, and handling errors gracefully."
  )

  .addSubHeading("Core Components")
  .addBulletList([
    "AWS S3 Data Landing Bucket - Consolidated entry point for all machine logs",
    "AWS Step Functions - Orchestrates the entire validation workflow",
    "AWS Lambda - Metadata extraction and prerequisites checking",
    "Error Handling Module - Comprehensive error detection and notifications"
  ])

  .addSubHeading("Data Landing Zone Structure")
  .addCodeBlock(
    `s3://robotics-data-landing/
├── robots/
│   ├── robot-001/
│   │   ├── 2025-12-23/
│   │   │   ├── logs_001.json      (Raw machine logs)
│   │   │   ├── events_001.json    (Event streams)
│   │   │   └── metrics_001.csv    (Sensor telemetry)
│   ├── robot-002/
│   └── robot-003/
└── _archived/
    └── {Data >90 days, lifecycle pruned}`,
    'text'
  )

//   .addSubHeading("Step Function Workflow")
//   .addImageBlock(
//     require("../components/blog/PVS/arch.png"),
//     'at_txt',
//     'Phase 1 Step Functions workflow with validation and error handling paths',
//     { size: 'contained', align: 'center' }
//   )

  .addSubHeading("Error Handling Strategy")
  .addTable({
    headers: ["Error Type", "Detection Method", "Handling", "Notification"],
    rows: [
      ["Schema Mismatch", "Schema validator", "Reject + quarantine", "Email + Teams"],
      ["Corrupted Data", "Checksum validation", "Move to DLQ", "Immediate alert"],
      ["Missing Fields", "Field validator", "Attempt recovery", "Log + retry"],
      ["Size Exceeded", "Volume check", "Chunk & process", "None (expected)"],
      ["Permission Error", "Access control", "Escalate", "Page engineer"]
    ]
  })

  .addParagraph(
    "Notification channels include email to the engineering team, Teams webhooks with detailed context, CloudWatch Logs for audit trails, and DynamoDB failure registry for historical analysis."
  )

  // ============================================
  // PHASE 2: EXTRACTION & PROCESSING
  // ============================================
  .addHeading("Phase 2: Serverless Log Extraction & Processing")
  .addParagraph(
    "Phase 2 transforms raw validated data into structured, enriched events ready for the enterprise data lake. It leverages serverless Docker containers and Databricks for scalable processing."
  )

  .addSubHeading("Extraction Pipeline Components")
  .addBulletList([
    "Docker Container on Lambda - Parses and processes raw log files",
    "Extraction Algorithm - Tokenization, parsing, schema mapping, enrichment",
    "Databricks Delta Lake - ACID-compliant data warehouse with Delta tables",
    "AWS SQS - Message queue for async processing and downstream subscriptions"
  ])

  .addSubHeading("Extraction Algorithm Flow")
  .addCodeBlock(
    `INPUT: Raw log file (JSON/CSV/TEXT)
│
├─→ STEP 1:  Tokenization
│   ├─ Split into records
│   ├─ Detect record boundaries
│   └─ Handle multi-line entries
│
├─→ STEP 2:  Parsing
│   ├─ Apply format-specific parsers
│   ├─ Extract key-value pairs
│   └─ Type inference (int, float, string, timestamp)
│
├─→ STEP 3: Schema Mapping
│   ├─ Match fields to schema
│   ├─ Handle missing fields (default values)
│   └─ Validate constraints
│
├─→ STEP 4: Enrichment
│   ├─ Add machine metadata (ID, type, location)
│   ├─ Add processing metadata (timestamp, version)
│   ├─ Calculate checksums
│   └─ Add correlation IDs
│
└─→ OUTPUT: Structured event stream

PERFORMANCE:
├─ Throughput: 100K+ records/minute
├─ Latency: <5 sec for 1GB file
├─ Memory: Optimized for 3GB Lambda
└─ Concurrency: 100+ parallel executions`,
    'text'
  )

  .addSubHeading("Databricks Delta Lake Integration")
  .addParagraph(
    "Scheduled Databricks jobs run every 15 minutes to transform and load data into the Engineering Data Lake using Delta Lake's ACID-compliant format."
  )

  .addCodeBlock(
    `CREATE TABLE robotics_events (
  event_id STRING,                    -- PK: unique event identifier
  machine_id STRING,                  -- Robot identifier
  timestamp TIMESTAMP,                -- Event occurrence time
  event_type STRING,                  -- Category: STARTUP, ERROR, SHUTDOWN, etc.
  event_data STRUCT<                  -- Nested event details
    duration_ms INT,
    status STRING,
    error_code STRING,
    custom_fields MAP<STRING, STRING>
  >,
  metadata STRUCT<                    -- Processing metadata
    extracted_at TIMESTAMP,
    extraction_version STRING,
    checksum STRING,
    source_file STRING,
    pipeline_id STRING
  >,
  ingestion_date DATE                 -- Partition: improves query performance
)
USING DELTA
PARTITIONED BY (ingestion_date, machine_id)
CLUSTERED BY (timestamp) INTO 100 BUCKETS
TBLPROPERTIES (
  'delta.targetFileSize' = '128mb',
  'delta.tuneFileSizesForRewrites' = 'true',
  'delta.enableChangeDataFeed' = 'true'
);`,
    'sql'
  )

  .addSubHeading("Event Messaging Pattern")
  .addParagraph(
    "Extraction metadata is published to AWS SQS in a structured JSON format for downstream systems to consume asynchronously."
  )

  .addCodeBlock(
    `{
  "extraction_id": "ext-20251223-001",
  "machine_id": "robot-001",
  "batch_timestamp": "2025-12-23T20:30:00Z",
  "event_count": 15420,
  "checksum": "abc123def456",
  "extraction_status": "SUCCESS",
  "file_locations": {
    "images": "s3://data-images/robot-001/2025-12-23/",
    "metadata": "s3://data-metadata/robot-001/2025-12-23/metadata.json"
  },
  "processing_time_ms": 4523,
  "skip_iaw_push": false
}`,
    'json'
  )

  // ============================================
  // PHASE 3: DOWNSTREAM CONSUMPTION
  // ============================================
  .addHeading("Phase 3: Downstream Data Consumption")
  .addParagraph(
    "Processed data flows to multiple downstream systems, each optimized for specific use cases and consumption patterns."
  )

  .addSubHeading("Downstream Systems")

  .addHeading("IAW - Image Analysis Workflow", 3)
  .addBulletList([
    "Purpose: Extract visual insights from robot operation images",
    "Input: Image files stored in S3 from Phase 2",
    "Process: Computer vision models, object detection, anomaly detection",
    "Output: Structured image metadata and insights",
    "SLA: <1 hour processing window"
  ])

  .addHeading("EAW - Engineering Analytics Workflow", 3)
  .addBulletList([
    "Purpose: Analytical metadata from CAN bus logs",
    "Input: CAN (Controller Area Network) log extraction",
    "Process: Statistical analysis, trend detection, anomaly identification",
    "Output: Analytics-ready tables in EDL",
    "SLA: <30 min processing window"
  ])

  .addHeading("EDL - Enterprise Data Lake", 3)
  .addBulletList([
    "Purpose: Single source of truth for all robotics data",
    "Storage: Databricks Delta Lake tables",
    "Data Retention: 2 years hot, 5 years cold archive",
    "Accessibility: SQL, Python, Scala, Spark APIs",
    "Performance: Optimized for OLAP queries"
  ])

  // ============================================
  // COST ANALYSIS & ROI
  // ============================================
  .addHeading("Cost Analysis & Financial Impact")
  .addParagraph(
    "One of the most significant achievements was reducing operational costs by 73% while simultaneously improving reliability and data quality."
  )

  .addSubHeading("Before vs After Cost Breakdown")
  .addTable({
    headers: ["Component", "Before", "After", "Monthly Savings"],
    rows: [
      ["S3 Storage", "$2,400", "$1,200", "$1,200"],
      ["Compute (EC2/Lambda)", "$4,600", "$600", "$4,000"],
      ["Data Transfer", "$1,200", "$150", "$1,050"],
      ["Databricks/Processing", "-", "$1,800", "-"],
      ["DynamoDB", "$600", "$250", "$350"],
      ["Monitoring & Logging", "-", "$100", "-"],
      ["Manual Effort (3 eng @ 20%)", "$6,000", "$1,000", "$5,000"],
      ["TOTAL MONTHLY", "$14,800", "$4,000", "$10,800"],
    ]
  })

  .addSubHeading("ROI Calculation")
  .addBulletList([
    "Development Investment: 400 engineer hours @ $75/hr = $30,000",
    "Infrastructure Setup: $5,000",
    "Testing & Validation: $3,000",
    "Total Investment: $38,000",])
    .addParagraph("---------------------------")
    .addBulletList(["Annual Savings: $129,600 (12 months × $10,800)",
    "Payback Period: 3.5 months",
    "Year 1 ROI: 341%",
    "3-Year Savings: $389,000 (with 5% growth)"
  ])

  // ============================================
  // TECHNOLOGY STACK
  // ============================================
  .addHeading("Technology Stack & Architecture")
  .addParagraph(
    "The technology stack was carefully chosen to balance scalability, cost-efficiency, operational simplicity, and enterprise-grade reliability."
  )

  .addSubHeading("Cloud & Infrastructure")
  .addBulletList([
    "AWS S3 - Object storage for data landing and archival",
    "AWS Lambda - Serverless compute for metadata extraction and processing",
    "AWS Step Functions - Workflow orchestration with visual debugging",
    "AWS SQS - Managed message queue for event streaming",
    "AWS DynamoDB - NoSQL database for failure registry and audit logs",
    "AWS CloudWatch - Monitoring, logging, and alerting"
  ])

  .addSubHeading("Data Processing & Analytics")
  .addBulletList([
    "Apache Spark - Distributed computing engine for large-scale data processing",
    "Databricks - Managed Spark platform with Delta Lake integration",
    "Delta Lake - ACID-compliant data warehouse with time-travel capabilities",
    "Docker - Containerization for portable, reproducible processing"
  ])

  .addSubHeading("Development & DevOps")
  .addBulletList([
    "Python 3.9+ - Core ingestion and processing logic",
    "SQL - Data transformations and queries",
    "Scala - Performance-critical path optimizations",
    "Terraform - Infrastructure as Code for reproducible deployments",
    "Git - Version control and collaboration",
    "GitHub Actions - CI/CD pipelines for automated testing and deployment"
  ])

  .addSubHeading("Technology Decision Matrix")
  .addTable({
    headers: ["Component", "Choice", "Rationale", "Alternatives"],
    rows: [
      ["Orchestration", "Step Functions", "Native AWS, serverless, visual workflows, built-in error handling", "Airflow, Prefect, Dagster"],
      ["Storage", "S3 + Databricks", "Scalability, cost-effective, Delta Lake support, ACID guarantees", "GCS, Azure Data Lake, Snowflake"],
      ["Processing Engine", "Apache Spark", "Distributed computing, fault-tolerant, established ecosystem, mature library", "Flink, Ray, Dask"],
      ["Data Warehouse", "Delta Lake", "ACID transactions, time-travel, optimized performance, Z-order clustering", "Snowflake, BigQuery, Redshift"],
      ["Messaging", "AWS SQS", "Fully managed, decoupled communication, cost-effective, 99.9% durability", "RabbitMQ, Kafka, SNS"],
      ["Containers", "Docker", "Standard containerization, portable across environments, ecosystem maturity", "Podman, containerd"]
    ]
  })

  // ============================================
  // PERFORMANCE & METRICS
  // ============================================
  .addHeading("Performance & Reliability Metrics")
  .addParagraph(
    "The system was designed to meet enterprise SLAs while maintaining cost efficiency and data quality standards."
  )

  .addSubHeading("Processing Performance")
  .addTable({
    headers: ["Metric", "Achieved Value", "Target/Benchmark"],
    rows: [
      ["Throughput", "100K+ records/minute", "Industry: 50K-150K"],
      ["P50 Latency", "2 seconds", "<5s target"],
      ["P95 Latency", "4 seconds", "<10s target"],
      ["P99 Latency", "6 seconds", "<15s target"],
      ["Error Rate", "<0.1%", "<1% acceptable"],
      ["Data Completeness", "99.7%", ">99% required"]
    ]
  })

  .addSubHeading("Reliability Metrics")
  .addTable({
    headers: ["Metric", "Achievement"],
    rows: [
      ["Uptime SLA", "99.9%"],
      ["MTTR (Mean Time to Recovery)", "<5 minutes"],
      ["Data Quality Score", "98%"],
      ["Validation Success Rate", "99.9%"],
      ["Duplicate Detection Rate", "99.5%"]
    ]
  })

  // ============================================
  // MONITORING & OBSERVABILITY
  // ============================================
  .addHeading("Monitoring, Alerting & Observability")
  .addParagraph(
    "Complete visibility into pipeline health was essential for maintaining reliability and catching issues before they impacted downstream systems."
  )

  .addSubHeading("Key Monitoring Dashboards")

  .addHeading("Phase 1 Dashboard", 3)
  .addBulletList([
    "Incoming file count and trends",
    "Success/failure rates by error type",
    "Average processing time and P95/P99 latencies",
    "Error distribution and root cause analysis"
  ])

  .addHeading("Phase 2 Dashboard", 3)
  .addBulletList([
    "Docker container execution time",
    "Record parsing success rate",
    "Schema validation results",
    "Enrichment completion status"
  ])

  .addHeading("Operational Dashboard", 3)
  .addBulletList([
    "Cost tracking and hourly burn rate",
    "SQS queue depth and message age",
    "Databricks job status and duration",
    "Alert firing rate and on-call burden"
  ])

  .addSubHeading("Alerting Rules & Thresholds")
  .addCodeBlock(
    `IF processing_latency > 30 minutes THEN alert "LATENCY_HIGH"
IF error_rate > 2% THEN page on_call_engineer
IF data_quality < 95% THEN quarantine_batch AND notify_team
IF cost_daily_burn > baseline * 1.2 THEN alert_billing_team
IF sqs_queue_depth > 10k THEN trigger_autoscaling`,
    'text'
  )

  // ============================================
  // LESSONS LEARNED
  // ============================================
  .addHeading("Lessons Learned & Best Practices")
  .addParagraph(
    "Building this system taught me valuable lessons about scalable data architecture, operational excellence, and cost optimization."
  )

  .addSubHeading("What Worked Well ✅")
  .addBulletList([
    "Event-driven architecture - Eliminated polling patterns, reduced latency to near real-time",
    "Serverless-first approach - Minimal ops overhead, automatic scaling, pay-per-use model",
    "Two-phase design - Phase 1 and Phase 2 could be optimized independently",
    "Comprehensive error handling - No silent failures, immediate visibility into issues",
    "Delta Lake adoption - ACID guarantees prevented data inconsistencies and corruption"
  ])

  .addSubHeading("Challenges & Solutions")
  .addTable({
    headers: ["Challenge", "Root Cause", "Solution", "Impact"],
    rows: [
      ["Lambda timeout", "Single Lambda processing large batches", "Switch to Step Functions + Docker containers", "✅ Unlimited processing time"],
      ["Cold starts", "Standard Lambda instances", "Provisioned concurrency + connection pooling", "✅ <1s startup latency"],
      ["Cost spikes", "No cost monitoring or limits", "Implemented cost alerts + spot pricing", "✅ 73% cost reduction"],
      ["Data inconsistencies", "No validation in Phase 1", "Added schema validation and checksum checks", "✅ 98% quality score"],
      ["Debugging failures", "Minimal logging and tracing", "Integrated CloudWatch X-Ray + structured logs", "✅ <5 min debug time"]
    ]
  })

  .addSubHeading("Future Enhancements 🚀")
  .addBulletList([
    "Real-time streaming - Integrate Kinesis for sub-second latency requirements",
    "ML-based quality assurance - Automatic anomaly detection and data quality scoring",
    "Auto-remediation - Automatically fix common data quality issues",
    "Multi-region deployment - Support global data ingestion across regions",
    "GraphQL API - Provide flexible, client-driven data access layer"
  ])

  // ============================================
  // BUSINESS IMPACT
  // ============================================
  .addHeading("Business & Technical Impact")
  .addParagraph(
    "The project delivered significant business value across multiple dimensions including cost, reliability, and organizational capability."
  )

  .addSubHeading("Operational Achievements")
  .addBulletList([
    "96% faster processing (48 hours → 2 hours) - Real-time insights instead of batch delays",
    "73% cost reduction ($129.6K annually) - Significant bottom-line impact",
    "99.9% uptime SLA - Enterprise-grade reliability",
    "83% less manual effort - Engineers freed up for strategic work"
  ])

  .addSubHeading("Data Quality Improvements")
  .addBulletList([
    "98% validation success rate - Consistent, high-quality data",
    "99.7% data completeness - No missing critical fields",
    "99.5% duplicate detection - Accurate, deduplicated datasets",
    "Real-time error visibility - Issues caught and alerted within seconds"
  ])

  .addSubHeading("Team & Organization Impact")
  .addBulletList([
    "Self-service data access - Non-technical stakeholders can query data via SQL",
    "Reduced on-call burden - Fewer production incidents and escalations",
    "Faster incident response - Clear monitoring and alerting enable quick fixes",
    "Better data-driven decisions - Reliable, timely data for business analysis"
  ])

  // ============================================
  // KEY TAKEAWAYS
  // ============================================
  .addHeading("Key Takeaways & Conclusions")
  .addParagraph(
    "Building a scalable, reliable, cost-efficient ingestion platform demonstrated the power of thoughtful architecture and automation."
  )

  .addSubHeading("Architectural Principles Applied")
  .addBulletList([
    "Serverless-first - Pay for execution, not infrastructure",
    "Event-driven - React to changes, not poll continuously",
    "Loosely coupled - Each phase independent, scales separately",
    "Fail-safe - Comprehensive error handling and alerting",
    "Observable - Complete visibility into pipeline health"
  ])

  .addSubHeading("Engineering Lessons")
  .addBulletList([
    "Invest in error handling and monitoring early - Catches issues before they escalate",
    "Use managed services - Reduces operational overhead and maintenance burden",
    "Separate concerns - Independent scaling and optimization of each phase",
    "Automate validation - Catch quality issues at ingestion, not consumption",
    "Track costs religiously - Small optimizations compound over time"
  ])

  .addSubHeading("Results")
  .addParagraph(
    "By building a scalable, reliable, cost-efficient ingestion platform, I transformed a pain point (manual data processing) into a competitive advantage (enterprise data infrastructure). The system now processes 100+ TB/month with minimal operational overhead and enables data-driven decision-making across the organization."
  )

  // ============================================
  // TECHNICAL RESOURCES
  // ============================================
  .addHeading("Technical Resources & References")
  .addParagraph(
    "The implementation leveraged industry-standard best practices and documentation from leading cloud and data engineering communities."
  )

  .addSubHeading("AWS Services Documentation")
  .addBulletList([
    "[Step Functions Documentation](https://docs.aws.amazon.com/step-functions/)",
    "[Lambda Performance Optimization](https://docs.aws.amazon.com/lambda/latest/dg/best-practices.html)",
    "[S3 Best Practices](https://docs.aws.amazon.com/s3/latest/userguide/)"
  ])

  .addSubHeading("Data Technologies")
  .addBulletList([
    "[Apache Spark Documentation](https://spark.apache.org/docs/latest/)",
    "[Databricks Delta Lake Guide](https://docs.databricks.com/delta/)",
    "[Docker Best Practices](https://docs.docker.com/develop/dev-best-practices/)"
  ])

// Export the blog post
// module.exports = commonIngestionPlatform;

// Create authored posts with hero images (placeholders) and metadata
const nebula = new BlogBuilder({
  title: "Nebula Serverless Ingestion",
  image: require("../components/blog/PVS/arch.png"),
  description: "Event-driven serverless ingestion platform for high-throughput CV/ML datasets.",
})
  .setPublished("4 July 2024")
  .setReadTime(6)
  .setAuthor({
    name: "Shubham Gupta",
    logo: require("../editable-stuff/prof.jpg"),
    designation: "Lead Data Engineer, Deere & Co",
  })
  .addHeading("Overview")
  .addParagraph(
    "Nebula is a serverless, event-driven ingestion platform designed to accept, validate, and land large volumes of computer-vision datasets (images, annotations, and telemetry) with minimal operational overhead. The platform focuses on reliability, cost efficiency, and rapid onboarding for new projects."
  )
  .addHeading("Architecture & Key Components")
  .addParagraph(
    "Nebula uses S3 as the canonical landing area, Lambda/SQS for orchestration, Step Functions for workflows, Terraform for infra, and Databricks for large-scale transforms and validations."
  )
  .addImageBlock(require("../components/blog/PVS/arch.png"),'at_txt', 'Optional caption', { size: 'contained', align: 'center' })

  .addParagraph(
    "Designing for 1TB+ daily ingestion required event-driven patterns, idempotent processing, and strong observability."
  );

const commonIngest = new BlogBuilder({
  title: "Common Ingest Framework",
  image: null,
  description: "Unified multi-tenant ingestion framework with policy-as-code and adapters.",
})
  .setPublished("4 July 2024")
  .setReadTime(5)
  .setAuthor({
    name: "Shubham Gupta",
    logo: require("../editable-stuff/prof.jpg"),
    designation: "Lead Data Engineer, \nDeere & Co",
  })
  .addHeading("Why a Common Framework?")
  .addParagraph(
    "A single reusable pattern reduced duplicated effort, provided consistent metadata contracts, and enabled faster onboarding."
  )
  .addHeading("Implementation")
  .addParagraph(
    "Adapters normalize payloads into a canonical format, a controller orchestrates validation/enrichment/landing, and Terraform ensures repeatable provisioning.")
  .addParagraph("We use Step Functions for orchestration and SQS for buffering.");

const prelabel = new BlogBuilder({
  title: "Pre-Labeling & ROI Pipelines",
  image: null,
  description: "Automation pipelines that reduce manual labeling through pre-labeling and active learning.",
})
  .setPublished("4 July 2024")
  .setReadTime(4)
  .addHeading("Approach")
  .addParagraph(
    "We combine heuristic filters, lightweight inferencing, and active learning to triage examples and maximize labeling ROI."
  )
  .addHeading("Results")
  .addParagraph("66% reduction in labeling time; $380K+ annual savings.");

const pvs = new BlogBuilder({
  title: "PVS 3.0",
  image: null,
  description: "Parallel validation system for CV/ML workloads that accelerates evaluation.",
})
  .setPublished("4 July 2024")
  .setReadTime(5)
  .addHeading("What is PVS?")
  .addParagraph(
    "A serverless validation framework that runs massive parallel evaluation jobs, aggregates results into Delta tables, and provides transparent, reproducible validation runs."
  );

const aletheia = new BlogBuilder({
  title: "Aletheia Analytics Platform",
  image: null,
  description: "Dataset management, unified search, and Labelbox integration for labeling at scale.",
})
  .setPublished("4 July 2024")
  .setReadTime(5)
  .addHeading("Platform Goals")
  .addParagraph(
    "Centralize dataset discovery, analytics, and labeling orchestration to improve reuse and reduce duplicate work."
  );

const governance = new BlogBuilder({
  title: "Data Governance",
  image: null,
  description: "Migration to Unity Catalog and enterprise governance for auditability.",
})
  .setPublished("4 July 2024")
  .setReadTime(4)
  .addHeading("Implementation")
  .addParagraph(
    "Migrated critical workflows to Unity Catalog, implemented policy-as-code, and delivered dataset-level auditability across regions."
  );

const finalBlogList = [commonIngestionPlatform, commonIngest, prelabel, pvs, aletheia, governance];

export default finalBlogList;
